import { AlertCircle, Loader2, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  usePromotionalAdDestinationCategories,
  useTranslation,
  type AdminStorePromotionalAdDestination,
  type PromotionalAdDestinationCategoryOption,
  type TranslationKey,
} from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

export interface PromotionalAdCategoryPickerProps {
  readonly storeId: string | null;
  readonly selectedCategoryId: string | null;
  readonly initialDestination?: AdminStorePromotionalAdDestination | null;
  readonly disabled?: boolean;
  readonly errorKey?: TranslationKey | null;
  readonly onSelect: (categoryId: string) => void;
  readonly onClear: () => void;
}

function getInitialCategory(
  destination: AdminStorePromotionalAdDestination | null | undefined,
): PromotionalAdDestinationCategoryOption | null {
  if (destination?.type !== 'category') return null;
  return {
    id: destination.categoryId,
    name: destination.categoryName ?? destination.categoryId,
    productCount: 0,
    isSelectable: destination.isAvailable,
  };
}

export function PromotionalAdCategoryPicker({
  storeId,
  selectedCategoryId,
  initialDestination,
  disabled = false,
  errorKey,
  onSelect,
  onClear,
}: PromotionalAdCategoryPickerProps) {
  const { t, dir } = useTranslation();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 250);
  const query = usePromotionalAdDestinationCategories(supabase, {
    storeId,
    search: debouncedSearch,
  });
  const initialCategory = getInitialCategory(initialDestination);

  const categories = useMemo(() => {
    const rows = query.data ?? [];
    if (
      initialCategory &&
      selectedCategoryId === initialCategory.id &&
      !rows.some((row) => row.id === initialCategory.id)
    ) {
      return [initialCategory, ...rows];
    }
    return rows;
  }, [initialCategory, query.data, selectedCategoryId]);

  const selected = categories.find((category) => category.id === selectedCategoryId) ?? null;

  return (
    <div dir={dir} className="space-y-3" data-testid="promotional-ad-category-picker">
      <div>
        <Label htmlFor="promotional-ad-category-search" className="mb-1.5 block text-start">
          {t('admin.promotionalAds.destination.categorySearchLabel' as TranslationKey)}
        </Label>
        <div className="relative">
          <Search
            className="text-muted-foreground pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            id="promotional-ad-category-search"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            className="ps-9"
            disabled={disabled}
            placeholder={t(
              'admin.promotionalAds.destination.categorySearchPlaceholder' as TranslationKey,
            )}
            data-testid="promotional-ad-category-search"
          />
        </div>
      </div>

      {selected ? (
        <div className="border-border bg-admin-surface-muted flex items-start justify-between gap-3 rounded-lg border p-3 text-start">
          <div className="min-w-0">
            <p className="text-foreground text-sm font-medium">
              {t('admin.promotionalAds.destination.selectedCategory' as TranslationKey, {
                name: selected.name,
              })}
            </p>
            {!selected.isSelectable ? (
              <p className="text-muted-foreground mt-1 text-xs">
                {t('admin.promotionalAds.destination.categoryUnavailable' as TranslationKey)}
              </p>
            ) : null}
          </div>
          <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={onClear}>
            <X className="size-4" aria-hidden />
            <span>{t('admin.promotionalAds.destination.clearSelection' as TranslationKey)}</span>
          </Button>
        </div>
      ) : null}

      {errorKey ? (
        <p role="alert" className="text-destructive text-start text-xs">
          {t(errorKey)}
        </p>
      ) : null}

      {query.isLoading ? (
        <div className="text-muted-foreground flex items-center gap-2 text-start text-sm">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          <span>{t('admin.promotionalAds.pickers.loadingCategories' as TranslationKey)}</span>
        </div>
      ) : null}

      {query.isError ? (
        <div
          role="alert"
          className="border-destructive/25 bg-destructive/10 text-destructive flex items-start justify-between gap-3 rounded-lg border p-3 text-start text-sm"
        >
          <span className="flex min-w-0 items-start gap-2">
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
            {t('common.errors.unknown' as TranslationKey)}
          </span>
          <Button type="button" variant="outline" size="sm" onClick={() => void query.refetch()}>
            {t('admin.promotionalAds.pickers.retryCategories' as TranslationKey)}
          </Button>
        </div>
      ) : null}

      {!query.isLoading && !query.isError && categories.length === 0 ? (
        <p className="text-muted-foreground rounded-lg border border-dashed p-4 text-start text-sm">
          {t('admin.promotionalAds.destination.categoryEmpty' as TranslationKey)}
        </p>
      ) : null}

      {categories.length > 0 ? (
        <div className="border-border divide-border max-h-72 overflow-y-auto rounded-lg border">
          {categories.map((category) => {
            const isSelected = category.id === selectedCategoryId;
            const isUnavailable = !category.isSelectable;
            return (
              <button
                key={category.id}
                type="button"
                disabled={disabled || isUnavailable}
                onClick={() => onSelect(category.id)}
                className={cn(
                  'hover:bg-admin-surface-muted flex w-full items-start justify-between gap-3 px-4 py-3 text-start text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                  isSelected && 'bg-admin-surface-muted',
                )}
                data-testid={`promotional-ad-category-option-${category.id}`}
              >
                <span className="min-w-0">
                  <span className="text-foreground block font-medium">{category.name}</span>
                  <span className="text-muted-foreground block text-xs">
                    {isUnavailable
                      ? t('admin.promotionalAds.destination.categoryUnavailable' as TranslationKey)
                      : category.productCount}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
